import { useState } from 'react'
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import BottomNavigation from '@mui/material/BottomNavigation'
import BottomNavigationAction from '@mui/material/BottomNavigationAction'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import Fab from '@mui/material/Fab'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useQueryClient } from '@tanstack/react-query'
import { PERMISSIONS } from '@jupi/shared'
import { BRAND, BrandHeader, BuildStrip, ClockDisplay, IconButton, ThemeModeToggle, useAuth, UserAvatar } from '@jupi/ui'
import MemberBanner from './MemberBanner'
import { activeItem, areaForPath, visibleAreas, type Area } from '../config/areas'

export const DRAWER_WIDTH = 240
// Height of the phone's bottom bar, which the page content and the FAB
// both have to clear.
import { useBookingLinkState } from '../features/calendar/bookingNavigation'
import { BOTTOM_BAR } from '../config/layout'

// The booking form, as a page or as the dialog over another page: the button
// that opens it has no business being there.
const BOOKING_FORM = /^\/bookings\/(new|\d+)$/


// The member app's frame. Navigation is two-level and context-sensitive
// (see config/areas.ts):
//
//   desktop (md+)  area tabs in the AppBar; the drawer lists the current
//                  area's pages only
//   phone          areas in the bottom bar; the current area's pages as a
//                  scrollable tab strip under the AppBar, and its primary
//                  action (New booking) as a floating button
//
// Profile, Admin and sign-out live behind the avatar on both.
export default function AppShell() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const location = useLocation()
  const navigate = useNavigate()
  const bookingLinkState = useBookingLinkState()
  const { user, logout, can } = useAuth()
  const queryClient = useQueryClient()

  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null)

  const areas = visibleAreas(can)
  const area = areaForPath(areas, location.pathname)
  const current = activeItem(area, location.pathname)
  const tabItems = area?.items.filter((item) => !item.fab) ?? []
  const fabItem = area?.items.find((item) => item.fab)

  const handleSignOut = async () => {
    setUserMenuAnchor(null)
    await logout()
    // Includes the persisted flight planner cache (main.tsx): the next
    // member on this device must not see this one's plan.
    queryClient.clear()
    navigate('/login')
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      {isDesktop ? (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          <Toolbar sx={{ bgcolor: BRAND.nightNavy, color: '#fff', borderBottom: `3px solid ${BRAND.beaconAmber}` }}>
            <BrandHeader />
          </Toolbar>
          <BuildStrip />
          {area ? <AreaList area={area} activeTo={current?.to} /> : <AreaIndex areas={areas} />}
        </Drawer>
      ) : null}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky" elevation={0}>
          <Toolbar sx={{ gap: 1 }}>
            {isDesktop ? (
              <Tabs
                value={area?.id ?? false}
                textColor="inherit"
                sx={{ '& .MuiTabs-indicator': { bgcolor: 'secondary.main' } }}
              >
                {areas.map((a) => (
                  <Tab
                    key={a.id}
                    value={a.id}
                    component={RouterLink}
                    to={a.items[0]?.to ?? a.basePath}
                    label={t(a.labelKey)}
                    icon={<Icon icon={a.icon} width={20} />}
                    iconPosition="start"
                    sx={{ minHeight: 64 }}
                  />
                ))}
              </Tabs>
            ) : (
              <BrandHeader compact />
            )}
            <Box sx={{ flex: 1 }} />
            <ClockDisplay />
            <ThemeModeToggle />
            <IconButton color="inherit" onClick={(e) => setUserMenuAnchor(e.currentTarget)} aria-label={t('nav.profile')}>
              <UserAvatar userId={user?.id} avatarVersion={user?.avatarVersion} email={user?.email ?? ''} displayName={user?.displayName} avatarStyle={user?.avatarStyle} size={32} />
            </IconButton>
          </Toolbar>
          {!isDesktop ? <BuildStrip thin /> : null}

          {!isDesktop && tabItems.length > 1 ? (
            <Tabs
              value={current && !current.fab ? current.to : false}
              variant="scrollable"
              scrollButtons={false}
              textColor="inherit"
              sx={{
                bgcolor: 'rgba(0,0,0,0.25)',
                minHeight: 44,
                '& .MuiTab-root': { minHeight: 44 },
                '& .MuiTabs-indicator': { bgcolor: 'secondary.main' },
              }}
            >
              {tabItems.map((item) => (
                <Tab key={item.to} value={item.to} component={RouterLink} to={item.to} label={t(item.labelKey)} />
              ))}
            </Tabs>
          ) : null}
        </AppBar>

        <MemberBanner />

        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem disabled sx={{ opacity: '1 !important' }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user?.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem component={RouterLink} to="/profile" onClick={() => setUserMenuAnchor(null)}>
            <ListItemIcon>
              <Icon icon="mdi:account-cog-outline" width={20} />
            </ListItemIcon>
            {t('nav.profile')}
          </MenuItem>
          {can(PERMISSIONS.expenseSubmit) ? (
            <MenuItem component={RouterLink} to="/profile/expenses" onClick={() => setUserMenuAnchor(null)}>
              <ListItemIcon>
                <Icon icon="mdi:receipt-text-outline" width={20} />
              </ListItemIcon>
              {t('nav.expenses')}
            </MenuItem>
          ) : null}
          {can(PERMISSIONS.invoiceRead) ? (
            <MenuItem component={RouterLink} to="/profile/invoices" onClick={() => setUserMenuAnchor(null)}>
              <ListItemIcon>
                <Icon icon="mdi:file-document-outline" width={20} />
              </ListItemIcon>
              {t('nav.invoices')}
            </MenuItem>
          ) : null}
          {can(PERMISSIONS.adminAccess) ? (
            <MenuItem component="a" href="/admin/">
              <ListItemIcon>
                <Icon icon="mdi:shield-account-outline" width={20} />
              </ListItemIcon>
              {t('nav.admin')}
            </MenuItem>
          ) : null}
          <MenuItem onClick={handleSignOut}>
            <ListItemIcon>
              <Icon icon="mdi:logout" width={20} />
            </ListItemIcon>
            {t('nav.logout')}
          </MenuItem>
        </Menu>

        <Box
          component="main"
          sx={{
            flex: 1,
            width: '100%',
            maxWidth: 1100,
            mx: 'auto',
            p: { xs: 1.5, sm: 3 },
            // Clear of the bottom bar, the FAB and the home indicator on a
            // phone. `sm` is repeated because the `p` above sets all four
            // sides from sm up and would otherwise drop this padding for
            // widths between 600 and 899 px, where the bottom bar still shows.
            pb: {
              xs: `calc(${BOTTOM_BAR + 72}px + env(safe-area-inset-bottom))`,
              sm: `calc(${BOTTOM_BAR + 72}px + env(safe-area-inset-bottom))`,
              md: 3,
            },
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* The one "new booking" button, on every screen size. Other areas'
          buttons stay phone-only: their pages keep a button of their own. */}
      {fabItem && current?.to !== fabItem.to && !BOOKING_FORM.test(location.pathname) && (!isDesktop || fabItem.to === '/bookings/new') ? (
        <Fab
          color="secondary"
          variant="extended"
          component={RouterLink}
          to={fabItem.to}
          state={fabItem.to === '/bookings/new' ? bookingLinkState : undefined}
          sx={{
            position: 'fixed',
            right: isDesktop ? 32 : 16,
            bottom: isDesktop ? 32 : `calc(${BOTTOM_BAR + 16}px + env(safe-area-inset-bottom))`,
            zIndex: (t2) => t2.zIndex.appBar,
          }}
        >
          <Icon icon={fabItem.icon} width={22} style={{ marginRight: 8 }} />
          {t(fabItem.labelKey)}
        </Fab>
      ) : null}

      {!isDesktop ? (
        <>

          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: (t2) => t2.zIndex.appBar,
              pb: 'env(safe-area-inset-bottom)',
            }}
          >
            <BottomNavigation showLabels value={area?.id ?? false} sx={{ height: BOTTOM_BAR }}>
              {areas.map((a) => (
                <BottomNavigationAction
                  key={a.id}
                  value={a.id}
                  component={RouterLink}
                  to={a.items[0]?.to ?? a.basePath}
                  label={t(a.labelKey)}
                  icon={<Icon icon={a.icon} width={22} />}
                  // MUI's 80 px minimum fits four areas on a 360 px phone,
                  // not five; share the width out instead.
                  sx={{
                    minWidth: 0,
                    px: 0.5,
                    '&.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                    '& .MuiBottomNavigationAction-label, & .MuiBottomNavigationAction-label.Mui-selected': {
                      fontSize: '0.7rem',
                    },
                  }}
                />
              ))}
            </BottomNavigation>
          </Paper>
        </>
      ) : null}
    </Box>
  )
}

// Desktop drawer: the current area's pages.
function AreaList({ area, activeTo }: { area: Area; activeTo: string | undefined }) {
  const { t } = useTranslation()
  return (
    <List subheader={<ListSubheader>{t(area.labelKey)}</ListSubheader>}>
      {area.items.map((item) => (
        <ListItemButton
          key={item.to}
          component={RouterLink}
          to={item.to}
          selected={item.to === activeTo}
          sx={{ mx: 1, borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Icon icon={item.icon} width={22} />
          </ListItemIcon>
          <ListItemText primary={t(item.labelKey)} />
        </ListItemButton>
      ))}
    </List>
  )
}

// Desktop drawer outside any area (the profile page): every area, so the
// drawer is never empty.
function AreaIndex({ areas }: { areas: Area[] }) {
  const { t } = useTranslation()
  return (
    <List>
      {areas.map((a) => (
        <ListItemButton key={a.id} component={RouterLink} to={a.items[0]?.to ?? a.basePath} sx={{ mx: 1, borderRadius: 1 }}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Icon icon={a.icon} width={22} />
          </ListItemIcon>
          <ListItemText primary={t(a.labelKey)} />
        </ListItemButton>
      ))}
    </List>
  )
}
